/**
 * ProjectDetail — header + two-column body: a tabbed main area
 * (Description · Tasks [List/Kanban] · Files) and a narrow People/Details rail.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "../../utils/toast";
import { ArrowLeft, Plus, Check, MessageCircle, Clock, FileText, ListChecks, FolderOpen, List, Columns, Trash2, Download } from "lucide-react";
import { downloadTasksCSV, downloadTasksExcel, downloadTasksPDF } from "./lib/taskExport";
import { wsApi, wsBase, assetUrl } from "./wsApi";
import { hasPermission } from "../../utils/permissionUtils";
import {
  TASK_STATUS, TASK_STATUS_ORDER, PRIORITY, PROJECT_STATUS, fmtDate, fmtDuration, canManageTask,
} from "./wsConstants";
import { T, Avatar, PrimaryButton, StatusBadge, Tabs, ConfirmDialog, selectChevron } from "../../components/em/ui";
import { ColorBadge, money, Pills, Skeleton, HoverTip, taskMemberLabel } from "./wsUi";
import TaskModal from "./TaskModal";
import DescriptionTab from "./project/DescriptionTab";
import FilesTab from "./project/FilesTab";
import ProjectSidebar from "./project/ProjectSidebar";

const PriorityTag = ({ priority }) => {
  const p = PRIORITY[priority] || PRIORITY.medium;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 6, background: p.bg, color: p.text, font: `600 10px ${T.font}`, textTransform: "uppercase", letterSpacing: "0.03em", whiteSpace: "nowrap" }}>
      {p.label}
    </span>
  );
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  // `manager` used to mean both "can edit this project" AND "can see its
  // margin". Those are two different permissions, and a project manager should
  // get the first without the second — so they are split here.
  const manager = hasPermission("manage_projects");
  const canSeeProfit = hasPermission("view_workspace_analytics");
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [profit, setProfit] = useState(null);
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [tab, setTab] = useState("tasks");
  const [taskView, setTaskView] = useState("list");
  const [filesKey, setFilesKey] = useState(0); // bump to refresh Files tab
  const [confirmTask, setConfirmTask] = useState(null); // task awaiting delete
  const [deletingTask, setDeletingTask] = useState(false);
  // Task filters (right rail). Narrow the list/kanban only — the header
  // stats keep counting every task on the project.
  const [filterUser, setFilterUser] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  const loadProject = useCallback(() => {
    wsApi.getProject(id).then(setProject).catch(() => toast.error("Could not load project"));
  }, [id]);
  const loadTasks = useCallback(() => {
    wsApi.projectTasks(id).then(setTasks).catch(() => {});
  }, [id]);

  useEffect(() => {
    setLoading(true);
    wsApi.getProject(id).then(setProject)
      .catch(() => toast.error("Could not load project"))
      .finally(() => setLoading(false));
    loadTasks();
    wsApi.users().then(setUsers).catch(() => {});
    if (canSeeProfit) {
      wsApi.projectProfitability(id).then(setProfit).catch(() => {});
      wsApi.projectBudget(id).then(setBudget).catch(() => {});
    }
  }, [id, loadTasks, canSeeProfit]);

  const refreshProfit = useCallback(() => {
    if (canSeeProfit) {
      wsApi.projectProfitability(id).then(setProfit).catch(() => {});
      wsApi.projectBudget(id).then(setBudget).catch(() => {});
    }
  }, [id, canSeeProfit]);

  // Time logged inside the open TaskModal (or a live timer stopping in the
  // header) has to refetch here — those paths can't use onSaved, which closes
  // the modal. Both fire `workspaceDataChanged`. loadProject() is part of this
  // now because "Hours logged" reads project.logged_minutes, not tasks[].
  useEffect(() => {
    const handler = () => { loadProject(); loadTasks(); refreshProfit(); };
    window.addEventListener("workspaceDataChanged", handler);
    return () => window.removeEventListener("workspaceDataChanged", handler);
  }, [loadProject, loadTasks, refreshProfit]);

  const moveTask = async (task, status) => {
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status } : t)));
    try { await wsApi.updateTaskStatus(task.id, status); refreshProfit(); }
    catch { toast.error("Could not update task"); loadTasks(); }
  };

  // Deleting takes the task's comments and logged time with it, so it asks
  // first, and the row goes only once the server has confirmed.
  const removeTask = async () => {
    if (!confirmTask) return;
    setDeletingTask(true);
    try {
      await wsApi.deleteTask(confirmTask.id);
      setTasks((prev) => prev.filter((t) => t.id !== confirmTask.id));
      refreshProfit();
      toast.success("Task deleted");
      setConfirmTask(null);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not delete task");
    } finally {
      setDeletingTask(false);
    }
  };

  if (loading) return (
    <div>
      <Skeleton h={150} radius={14} style={{ marginBottom: 16 }} />
      <div style={{ display: "flex", gap: 16 }}>
        <Skeleton h={360} radius={14} style={{ flex: 1 }} />
        <Skeleton w={300} h={280} radius={14} />
      </div>
    </div>
  );
  if (!project) return <div style={{ padding: 40 }}>Project not found.</div>;

  const done = tasks.filter((t) => t.status === "done").length;
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;
  // Project-wide total from the server, which counts time logged straight
  // against the project (no task) as well as task time. Summing tasks[] here
  // used to miss every Timesheets entry, since those carry no task_id — a
  // project could show "0m" while the burn-down below reported 13.4h.
  // Falls back to the task sum only if an older payload omits the field.
  const loggedMins =
    project?.logged_minutes != null
      ? Number(project.logged_minutes) || 0
      : tasks.reduce((s, t) => s + (Number(t.logged_minutes) || 0), 0);

  // Employee: anyone on the task (assignee, collaborator or watcher).
  // Dates: the task's due date must fall within the range; with a range set,
  // tasks without a due date drop out. Dates compare as 'YYYY-MM-DD' strings.
  const filtersOn = !!(filterUser || filterFrom || filterTo);
  const visibleTasks = tasks.filter((t) => {
    if (filterUser && !(t.members || []).some((m) => String(m.user_id) === filterUser)) return false;
    const due = t.due_date ? String(t.due_date).slice(0, 10) : "";
    if ((filterFrom || filterTo) && !due) return false;
    if (filterFrom && due < filterFrom) return false;
    if (filterTo && due > filterTo) return false;
    return true;
  });
  // Employees to filter by: the project's members plus anyone already on one
  // of its tasks (a member may since have been removed from the project).
  const filterPeople = (() => {
    const byId = new Map();
    (project.members || []).forEach((m) => byId.set(String(m.user_id), m.full_name?.trim() || m.username));
    tasks.forEach((t) => (t.members || []).forEach((m) => {
      // Task members carry full_name too (attachAssignees) — same name as above.
      if (!byId.has(String(m.user_id))) byId.set(String(m.user_id), m.full_name?.trim() || m.username);
    }));
    return [...byId.entries()]
      .map(([uid, name]) => ({ uid, name: name || `User #${uid}` }))
      .sort((a, b) => a.name.localeCompare(b.name));
  })();

  const TABS = [
    { key: "description", label: "Description", Icon: FileText },
    { key: "tasks", label: "Tasks", Icon: ListChecks },
    { key: "files", label: "Files", Icon: FolderOpen },
  ];

  return (
    <div>
      <button onClick={() => navigate(`${wsBase()}/projects`)}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: 0, color: T.fg5, font: `500 12px ${T.font}`, cursor: "pointer", marginBottom: 14, padding: 0 }}>
        <ArrowLeft size={14} /> All Projects
      </button>

      {/* Header card */}
      <div style={{ background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 16, padding: 22, boxShadow: T.shadowSm, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          {project.logo ? (
            <div style={{ width: 52, height: 52, borderRadius: 12, overflow: "hidden", flexShrink: 0, border: `1px solid ${T.border}` }}>
              <img src={assetUrl(project.logo)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          ) : (
            <ColorBadge color={project.color || T.orange} size={52} label={(project.name || "").slice(0, 1).toUpperCase()} />
          )}
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <h2 style={{ margin: 0, font: `700 22px ${T.font}`, letterSpacing: "-0.02em", color: T.fg1 }}>{project.name}</h2>
              <StatusBadge label={(PROJECT_STATUS[project.status] || PROJECT_STATUS.planning).label} />
            </div>
            {/* Client names run to 200 chars, often unbroken — wrap anywhere
                so the full name shows inside the header. */}
            <div style={{ font: `400 12px ${T.font}`, color: T.fg5, marginTop: 4, overflowWrap: "anywhere" }}>{project.client_name || "No client"}</div>
          </div>
          {tab === "tasks" && (
            <PrimaryButton icon={Plus} onClick={() => setModal({ create: true })}>New Task</PrimaryButton>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginTop: 20, paddingTop: 18, borderTop: `1px solid ${T.divider}` }}>
          {[
            { l: "Progress", v: `${pct}%` },
            { l: "Tasks done", v: `${done}/${tasks.length}` },
            { l: "Hours logged", v: fmtDuration(loggedMins) },
            { l: canSeeProfit && profit ? "Margin" : "Deadline", v: canSeeProfit && profit ? `${profit.margin}%` : (project.deadline ? fmtDate(project.deadline) : "—"), c: canSeeProfit && profit ? (profit.profit >= 0 ? T.success600 : T.danger) : undefined },
          ].map((s) => (
            <div key={s.l}>
              <div style={{ font: `700 18px ${T.font}`, color: s.c || T.fg1, fontVariantNumeric: "tabular-nums" }}>{s.v}</div>
              <div style={{ font: `500 11px ${T.font}`, color: T.fg7, marginTop: 3 }}>{s.l}</div>
            </div>
          ))}
        </div>
        {canSeeProfit && profit && (
          <div style={{ display: "flex", gap: 18, marginTop: 14, font: `500 12px ${T.font}` }}>
            <span style={{ color: T.success600 }}>Revenue {money(profit.revenue)}</span>
            <span style={{ color: T.danger }}>Cost {money(profit.cost)}</span>
            <span style={{ color: profit.profit >= 0 ? T.success600 : T.danger, fontWeight: 700 }}>Profit {money(profit.profit)}</span>
          </div>
        )}
        {canSeeProfit && budget && budget.budget > 0 && (() => {
          const over = budget.pct != null && budget.pct > 100;
          const barPct = Math.min(100, budget.pct || 0);
          const barColor = over ? T.danger : budget.pct > 80 ? "#f59e0b" : T.success600;
          return (
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.divider}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                <span style={{ font: `600 12px ${T.font}`, color: T.fg2 }}>
                  Budget burn-down{budget.budget_type ? ` · ${budget.budget_type}` : ""}
                </span>
                <span style={{ font: `600 12px ${T.font}`, color: barColor }}>
                  {money(budget.spent_cost)} / {money(budget.budget)} ({budget.pct}%)
                </span>
              </div>
              <div style={{ height: 10, borderRadius: 9999, background: T.bgMuted, overflow: "hidden" }}>
                <div style={{ width: `${barPct}%`, height: "100%", background: barColor, transition: "width .3s" }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, font: `500 11px ${T.font}`, color: T.fg7 }}>
                <span>{budget.logged_hours}h logged</span>
                <span style={{ color: over ? T.danger : T.fg7, fontWeight: over ? 700 : 500 }}>
                  {over ? `Over budget by ${money(Math.abs(budget.remaining))}` : `${money(budget.remaining)} remaining`}
                </span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Two-column body */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Tab bar — shared segmented control (same as Attendance/Leaves) */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <Tabs
              tabs={TABS.map((tb) => ({ key: tb.key, label: tb.label, icon: tb.Icon }))}
              active={tab} onChange={setTab}
            />
            {tab === "tasks" && (
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
                <TaskDownloadMenu
                  tasks={visibleTasks}
                  meta={{
                    projectName: project.name,
                    // Printed under the PDF title so a filtered export says so.
                    filters: [
                      filterUser && `Employee: ${filterPeople.find((p) => p.uid === filterUser)?.name || filterUser}`,
                      (filterFrom || filterTo) && `Due ${filterFrom || "…"} to ${filterTo || "…"}`,
                    ].filter(Boolean).join(", "),
                  }}
                />
                <Pills
                  options={[{ value: "list", label: "List", icon: List }, { value: "kanban", label: "Kanban", icon: Columns }]}
                  value={taskView} onChange={setTaskView}
                />
              </div>
            )}
          </div>

          {tab === "description" && (
            <DescriptionTab projectId={id} initialDoc={project.description_doc} onFilesChanged={() => setFilesKey((k) => k + 1)} />
          )}
          {tab === "files" && <FilesTab key={filesKey} projectId={id} canManage={manager} />}
          {tab === "tasks" && (
            <TasksPanel
              tasks={visibleTasks} view={taskView} onOpen={(taskId) => setModal({ taskId })}
              filtered={filtersOn && tasks.length > 0}
              onClearFilters={() => { setFilterUser(""); setFilterFrom(""); setFilterTo(""); }}
              onToggleDone={(t) => moveTask(t, t.status === "done" ? "todo" : "done")}
              onCreate={() => setModal({ create: true })}
              onMove={moveTask}
              // Shown per task: its creator, or a `delete_tasks` holder.
              onDelete={setConfirmTask}
            />
          )}
        </div>

        <div style={{ width: 300, flexShrink: 0, display: "flex", flexDirection: "column", gap: 16 }}>
          {tab === "tasks" && (
            <TaskFilters
              people={filterPeople}
              user={filterUser} from={filterFrom} to={filterTo}
              onUser={setFilterUser} onFrom={setFilterFrom} onTo={setFilterTo}
              active={filtersOn}
              shown={visibleTasks.length} total={tasks.length}
              onClear={() => { setFilterUser(""); setFilterFrom(""); setFilterTo(""); }}
            />
          )}
          <ProjectSidebar project={project} users={users} canManage={manager} onChanged={loadProject} />
        </div>
      </div>

      {modal && (
        <TaskModal
          taskId={modal.taskId}
          presetProjectId={id}
          projects={[project]}
          users={users}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); loadTasks(); refreshProfit(); }}
        />
      )}

      <ConfirmDialog
        open={!!confirmTask}
        title="Delete task?"
        message={`"${confirmTask?.title}" will be removed, along with its comments and logged time.`}
        confirmLabel="Delete task"
        danger
        busy={deletingTask}
        onConfirm={removeTask}
        onCancel={() => !deletingTask && setConfirmTask(null)}
      />
    </div>
  );
}

// index.css skins every bare <button> with height:40px / line-height:40px, so
// an icon button must state its own box — otherwise the glyph renders at the
// top of a 40px block and floats above the avatars it sits beside.
const rowIconBtn = {
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  padding: 0,
  margin: 0,
  lineHeight: 1,
  border: 0,
  borderRadius: 8,
  background: "none",
  color: T.fg7,
  cursor: "pointer",
};

// ── Download menu (PDF / Excel / CSV of the tasks currently shown) ────────────
function TaskDownloadMenu({ tasks, meta }) {
  const [open, setOpen] = useState(false);
  const disabled = !tasks.length;
  const run = (kind) => {
    setOpen(false);
    try {
      if (kind === "pdf") downloadTasksPDF(tasks, meta);
      else if (kind === "excel") downloadTasksExcel(tasks, meta);
      else downloadTasksCSV(tasks, meta);
    } catch (err) {
      console.error("Task export failed", err);
      toast.error("Could not export tasks");
    }
  };
  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        title={disabled ? "No tasks to export" : "Download the tasks shown"}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          height: 36, lineHeight: "normal", padding: "0 12px",
          borderRadius: T.rMd, border: `1px solid ${T.border}`,
          background: "var(--em-bg-card)", color: T.fg2, font: `600 12px ${T.font}`,
          cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
        }}
      >
        <Download size={14} /> Download ▾
      </button>
      {open && (
        <>
          {/* Click-away layer: any click outside the menu closes it. */}
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 19 }} />
          <div style={{
            position: "absolute", right: 0, top: "calc(100% + 4px)", zIndex: 20, minWidth: 140,
            background: "var(--em-bg-card)", border: `1px solid ${T.border}`,
            borderRadius: T.rMd, boxShadow: T.shadowMd, overflow: "hidden",
          }}>
            {[
              ["pdf", "PDF (.pdf)"],
              ["excel", "Excel (.xlsx)"],
              ["csv", "CSV (.csv)"],
            ].map(([kind, label]) => (
              <button
                key={kind}
                type="button"
                onClick={() => run(kind)}
                style={{
                  display: "block", width: "100%", height: "auto", lineHeight: "normal",
                  textAlign: "left", padding: "9px 14px", border: 0, borderRadius: 0,
                  background: "none", cursor: "pointer", font: `500 12.5px ${T.font}`, color: T.fg2,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = T.bgHead)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
              >
                {label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Task filters (right rail) ─────────────────────────────────────────────────
const filterLabel = { display: "block", font: `600 11px ${T.font}`, color: T.fg5, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 };
const filterInput = {
  width: "100%", height: 36, padding: "0 10px", boxSizing: "border-box",
  border: `1px solid ${T.border}`, borderRadius: 8, background: "var(--em-bg-card)",
  font: `400 13px ${T.font}`, color: T.fg1, outline: "none",
};

function TaskFilters({ people, user, from, to, onUser, onFrom, onTo, active, shown, total, onClear }) {
  return (
    <div style={{ background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 14, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
        <span style={{ flex: 1, font: `700 13px ${T.font}`, color: T.fg1 }}>Filters</span>
        {active && (
          <button type="button" onClick={onClear}
            style={{ height: "auto", lineHeight: "normal", padding: 0, border: 0, background: "none", color: T.orange, font: `600 12px ${T.font}`, cursor: "pointer" }}>
            Clear
          </button>
        )}
      </div>

      <label style={filterLabel} htmlFor="task-filter-user">Employee</label>
      <select id="task-filter-user" value={user} onChange={(e) => onUser(e.target.value)}
        style={{ ...filterInput, ...selectChevron, marginBottom: 14 }}>
        <option value="">All employees</option>
        {people.map((p) => <option key={p.uid} value={p.uid}>{p.name}</option>)}
      </select>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <label style={filterLabel} htmlFor="task-filter-from">Start date</label>
          <input id="task-filter-from" type="date" value={from} max={to || undefined}
            onChange={(e) => onFrom(e.target.value)} style={{ ...filterInput, minWidth: 0, padding: "0 8px" }} />
        </div>
        <div style={{ minWidth: 0 }}>
          <label style={filterLabel} htmlFor="task-filter-to">End date</label>
          <input id="task-filter-to" type="date" value={to} min={from || undefined}
            onChange={(e) => onTo(e.target.value)} style={{ ...filterInput, minWidth: 0, padding: "0 8px" }} />
        </div>
      </div>
      <div style={{ font: `400 11px ${T.font}`, color: T.fg7, marginTop: 8 }}>
        Matches tasks due between these dates.
      </div>

      {active && (
        <div style={{ font: `500 12px ${T.font}`, color: T.fg5, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.divider}` }}>
          Showing {shown} of {total} task{total === 1 ? "" : "s"}
        </div>
      )}
    </div>
  );
}

// ── Tasks panel (List + Kanban) ───────────────────────────────────────────────
function TasksPanel({ tasks, view, onOpen, onToggleDone, onCreate, onMove, onDelete, filtered, onClearFilters }) {
  if (tasks.length === 0 && filtered) {
    return (
      <div style={{ textAlign: "center", padding: "50px 20px", color: T.fg7, background: "var(--em-bg-card)", border: `1px dashed ${T.border}`, borderRadius: 14 }}>
        <div style={{ font: `600 15px ${T.font}`, color: T.fg1, marginBottom: 6 }}>No tasks match these filters</div>
        <button type="button" onClick={onClearFilters}
          style={{ height: "auto", lineHeight: "normal", padding: 0, border: 0, background: "none", color: T.orange, font: `600 13px ${T.font}`, cursor: "pointer" }}>
          Clear filters
        </button>
      </div>
    );
  }
  if (tasks.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "50px 20px", color: T.fg7, background: "var(--em-bg-card)", border: `1px dashed ${T.border}`, borderRadius: 14 }}>
        <div style={{ font: `600 15px ${T.font}`, color: T.fg1, marginBottom: 6 }}>No tasks yet</div>
        <div style={{ font: `400 13px ${T.font}`, marginBottom: 14 }}>Add the first task to get this project moving.</div>
        <div style={{ display: "inline-flex" }}><PrimaryButton icon={Plus} onClick={onCreate}>New Task</PrimaryButton></div>
      </div>
    );
  }

  const TaskRow = ({ t, i }) => {
    const assignees = (t.members || []).filter((m) => m.member_role === "assignee");
    const isDone = t.status === "done";
    return (
      <div onClick={() => onOpen(t.id)}
        style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderTop: i ? `1px solid ${T.divider}` : "none", cursor: "pointer" }}
        onMouseEnter={(e) => (e.currentTarget.style.background = T.bgHead)}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
        <button onClick={(e) => { e.stopPropagation(); onToggleDone(t); }}
          style={{ width: 18, height: 18, borderRadius: 9999, border: `2px solid ${isDone ? T.success600 : "#cbd5e1"}`, background: isDone ? T.success600 : "transparent", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
          {isDone && <Check size={11} color="#fff" />}
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: `500 13px ${T.font}`, color: T.fg1, textDecoration: isDone ? "line-through" : "none", opacity: isDone ? 0.6 : 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4, font: `400 11px ${T.font}`, color: T.fg7 }}>
            {t.comment_count > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><MessageCircle size={11} />{t.comment_count}</span>}
            {t.logged_minutes > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Clock size={11} />{fmtDuration(t.logged_minutes)}</span>}
          </div>
        </div>
        <PriorityTag priority={t.priority} />
        {t.due_date && <span style={{ font: `500 11px ${T.font}`, color: T.fg5, width: 64, textAlign: "right" }}>{fmtDate(t.due_date)}</span>}
        <div style={{ display: "flex" }}>
          {assignees.slice(0, 3).map((m, idx) => (
            <HoverTip key={m.user_id} label={taskMemberLabel(m)}
              style={{ marginLeft: idx ? -7 : 0, border: "2px solid var(--em-bg-card)", borderRadius: 9999 }}>
              <Avatar name={m.full_name || m.username} size={24} />
            </HoverTip>
          ))}
        </div>
        {onDelete && canManageTask(t) && (
          <button onClick={(e) => { e.stopPropagation(); onDelete(t); }}
            title="Delete task" aria-label={`Delete ${t.title}`}
            style={rowIconBtn}>
            <Trash2 size={14} />
          </button>
        )}
      </div>
    );
  };

  if (view === "kanban") {
    return <KanbanBoard tasks={tasks} onOpen={onOpen} onMove={onMove} onDelete={onDelete} />;
  }

  // List view — grouped by status
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {TASK_STATUS_ORDER.map((s) => {
        const col = TASK_STATUS[s];
        const list = tasks.filter((t) => t.status === s);
        if (!list.length) return null;
        return (
          <div key={s}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: 9999, background: col.dot }} />
              <span style={{ font: `700 12px ${T.font}`, color: T.fg1, textTransform: "uppercase", letterSpacing: "0.04em" }}>{col.label}</span>
              <span style={{ font: `600 11px ${T.font}`, color: T.fg7 }}>{list.length}</span>
            </div>
            <div style={{ background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
              {list.map((t, i) => <TaskRow key={t.id} t={t} i={i} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Kanban board (same look + drag-drop as My Tasks' Board view) ──────────────
// Declared at module level, not inside TasksPanel: a component declared inside
// another gets a new identity on every render, which remounts the cards and
// can pull the card being dragged out of the DOM mid-drag.
function KanbanCard({ t, onOpen, onDelete }) {
  const assignees = (t.members || []).filter((m) => m.member_role === "assignee");
  const overdue = t.due_date && new Date(t.due_date) < new Date() && t.status !== "done";
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text", String(t.id));
        // Gives the drag a "move" cursor rather than the default "copy".
        e.dataTransfer.effectAllowed = "move";
      }}
      onClick={() => onOpen(t.id)}
      style={{ background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 13px", cursor: "pointer", boxShadow: T.shadowSm }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 12px -4px rgba(10,10,10,0.15)")}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = T.shadowSm)}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <PriorityTag priority={t.priority} />
        <div style={{ flex: 1 }} />
        {onDelete && canManageTask(t) && (
          <button onClick={(e) => { e.stopPropagation(); onDelete(t); }}
            title="Delete task" aria-label={`Delete ${t.title}`}
            style={{ ...rowIconBtn, width: 22, height: 22 }}>
            <Trash2 size={13} />
          </button>
        )}
      </div>
      <div style={{ font: `500 13px/18px ${T.font}`, color: T.fg1, marginBottom: 10, wordBreak: "break-word" }}>{t.title}</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, font: `400 10px ${T.font}`, color: T.fg7 }}>
          {t.comment_count > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><MessageCircle size={11} />{t.comment_count}</span>}
          {t.logged_minutes > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Clock size={11} />{fmtDuration(t.logged_minutes)}</span>}
          {t.due_date && <span style={{ color: overdue ? T.danger : T.fg7 }}>{fmtDate(t.due_date)}</span>}
        </div>
        <div style={{ display: "flex" }}>
          {assignees.slice(0, 3).map((m, i) => (
            <HoverTip key={m.user_id} label={taskMemberLabel(m)}
              style={{ marginLeft: i ? -7 : 0, border: "2px solid var(--em-bg-card)", borderRadius: 9999 }}>
              <Avatar name={m.full_name || m.username} size={22} />
            </HoverTip>
          ))}
        </div>
      </div>
    </div>
  );
}

function KanbanBoard({ tasks, onOpen, onMove, onDelete }) {
  // Drop highlight painted straight onto the node — a state change would
  // re-render the board mid-drag for nothing.
  const paintColumn = (node, active) => {
    if (!node) return;
    node.style.background = active ? T.orange50 : T.bgMuted;
    node.style.borderColor = active ? T.orange : "transparent";
  };

  const dropOnColumn = (e, status) => {
    e.preventDefault();
    paintColumn(e.currentTarget, false);
    const taskId = Number(e.dataTransfer.getData("text"));
    if (!taskId) return;
    const task = tasks.find((t) => t.id === taskId);
    // Dropped back where it came from — not a status change.
    if (!task || task.status === status) return;
    onMove(task, status);
  };

  /* Column MINIMUM height = the rest of the viewport, so the empty space under
     the cards is still drop surface. Uses the board's document offset (not its
     viewport offset) so scrolling mid-drag doesn't resize the columns. */
  const boardRef = useRef(null);
  const [colHeight, setColHeight] = useState(420);
  useEffect(() => {
    const recalc = () => {
      const el = boardRef.current;
      if (!el) return;
      const docTop = el.getBoundingClientRect().top + window.scrollY;
      // Leave room for the floating dock fixed over the bottom of the page.
      setColHeight(Math.max(320, window.innerHeight - docTop - 104));
    };
    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, []);

  return (
    <div
      ref={boardRef}
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${TASK_STATUS_ORDER.length}, minmax(200px, 1fr))`,
        gap: 14,
        // `stretch` so every column reaches the floor and its empty area stays droppable.
        alignItems: "stretch",
        overflowX: "auto",
        paddingBottom: 8,
      }}
    >
      {TASK_STATUS_ORDER.map((s) => {
        const col = TASK_STATUS[s];
        const list = tasks.filter((t) => t.status === s);
        return (
          <div
            key={s}
            onDragOver={(e) => {
              // preventDefault on every dragover is what makes this a valid drop target.
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              paintColumn(e.currentTarget, true);
            }}
            onDragLeave={(e) => {
              // Ignore dragleave from crossing into a child (header, card).
              if (!e.currentTarget.contains(e.relatedTarget)) paintColumn(e.currentTarget, false);
            }}
            onDrop={(e) => dropOnColumn(e, s)}
            style={{
              background: T.bgMuted,
              border: "1px solid transparent",
              borderRadius: 14,
              padding: 10,
              display: "flex",
              flexDirection: "column",
              minHeight: colHeight,
              transition: "background 0.12s ease, border-color 0.12s ease",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "4px 6px 10px" }}>
              <span style={{ width: 8, height: 8, borderRadius: 9999, background: col.dot }} />
              <span style={{ font: `700 12px ${T.font}`, color: T.fg1 }}>{col.label}</span>
              <span style={{ font: `600 11px ${T.font}`, color: T.fg7 }}>{list.length}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
              {list.map((t) => <KanbanCard key={t.id} t={t} onOpen={onOpen} onDelete={onDelete} />)}
              {list.length === 0 && (
                <div
                  style={{
                    flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                    borderRadius: 10, border: `1px dashed ${T.border}`,
                    font: `500 11.5px ${T.font}`, color: T.fg7, textAlign: "center", padding: 12,
                    // The column owns the drop; this must not intercept the pointer.
                    pointerEvents: "none",
                  }}
                >
                  Drop tasks here
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
